import { DelegatedSessions } from '@/api/oversight/types';

type formatDateProps = 'absolute' | 'relative';

type Item = {
  value: string;
  label: string;
  active?: boolean;
};

type Filter = {
  id: number;
  label: string;
  paramKey: string;
  isActive: boolean;
  disabled?: boolean;
  items: Item[];
};

function formatDate(isoString: string, format: formatDateProps): string {
  const date = new Date(isoString);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 1000 / 60);
  const diffHours = Math.floor(diffMin / 60);

  const sameDay = (d1: Date, d2: Date): boolean =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const hour = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const formatedDate = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  if (format === 'absolute') {
    if (sameDay(date, now)) return `Hoje • ${hour}`;
    if (sameDay(date, yesterday)) return `Ontem • ${hour}`;
    return `${formatedDate} • ${hour}`;
  }

  if (format === 'relative') {
    if (diffMin < 1) return 'Agora';
    if (diffMin < 60) return `Há ${diffMin} min`;
    if (sameDay(date, now)) return `Há ${diffHours}h`;
    if (sameDay(date, yesterday)) return `Ontem • ${hour}`;
    return `Último • ${formatedDate}`;
  }

  return formatedDate;
}

const normalizeStr = (str: string): string =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Filtro temporário: a API ainda lista os usuários apagados no app.
const deletedUserPattern = /usuario[\s_-]*excluido/;

function isDeletedUser(...fields: (string | null | undefined)[]): boolean {
  return fields.some(
    (field) => !!field && deletedUserPattern.test(normalizeStr(field))
  );
}

const delegatedAccessLabels: Record<string, string> = {
  pending_consent: 'Consentimento pendente',
  approved: 'Solicita\u00e7\u00e3o aprovada',
  active: 'Sess\u00e3o delegada ativa',
  ended: 'Sess\u00e3o encerrada',
  rejected: 'Solicita\u00e7\u00e3o negada',
  expired: 'Solicita\u00e7\u00e3o expirada',
  canceled: 'Solicita\u00e7\u00e3o cancelada',
  revoked: 'Revogado',
};

const endReasonLabel: Record<string, string> = {
  manual: 'Encerrada manualmente',
  timeout: 'Tempo limite atingido',
  inactivity: 'Encerrada por inatividade',
  approval_expired: 'Prazo de início atingido',
  security_event: 'Encerrada pelo sistema',
  client_terminated: 'Encerrada pelo cliente',
};

// Session-related HTTPException subclasses raised by the API (app/base/errors/exceptions.py).
// error_type in event_metadata is always the raw exception class name (type(exc).__name__),
// so unmapped/unknown values fall back to the raw string instead of breaking the render.
const errorTypeLabels: Record<string, string> = {
  HTTP400Exception: 'Requisição inválida',
  HTTP401Exception: 'Sessão inválida',
  HTTP403Exception: 'Acesso não autorizado',
  HTTP404Exception: 'Sessão não encontrada',
  HTTP409Exception: 'Conflito de sessão',
  HTTP500Exception: 'Erro interno',
};

function getErrorTypeTitle(errorType?: string): string {
  if (!errorType) return 'Erro desconhecido';
  return errorTypeLabels[errorType] ?? errorType;
}

function expiredDetail(
  row: DelegatedSessions
): { label: string; tooltip: string } | null {
  if (row.status !== 'expired') {
    return null;
  }

  if (row.end_reason === 'approval_expired') {
    return {
      label: 'Prazo de início atingido',
      tooltip: 'Supervisor não iniciou o acesso delegado no prazo de 72 horas.',
    };
  }

  if (!row.consent_responded_at) {
    return {
      label: 'Prazo de resposta atingido',
      tooltip: 'Cliente não respondeu a solicitação no prazo de 24 horas.',
    };
  }

  return null;
}

const terminatedByLabels: Record<string, string> = {
  'support:user_id': 'Sessão encerrada pelo suporte',
  'system:inactivity': 'Sessão encerrada por inatividade',
  'user:logout': 'Sessão encerrada pelo usuário',
  'system:superseded': 'Sessão encerrada por novo login',
};

function getTerminatedByLabel(terminatedBy?: string): string {
  if (!terminatedBy) return 'Sessão encerrada';
  return terminatedByLabels[terminatedBy] ?? 'Sessão encerrada';
}

function buildParams(filters: Filter[], search: string): string {
  const params = new URLSearchParams();

  params.set('is_active', 'true');

  if (search) params.set('search', search);

  filters.forEach((filter) => {
    const activeItem = filter.items.find((item) => item.active);
    if (activeItem?.value && activeItem.value !== 'all') {
      params.set(filter.paramKey, activeItem.value);
    }
  });

  return params.toString();
}

// A coluna "Última sessão" mostra "Online" ou a data em que a última sessão
// terminou, então ela não é monotônica na própria chave: online é o valor mais
// recente da coluna e não está no timestamp — quem está online carrega a data da
// sessão anterior, ou nenhuma. Ordenar só pela chave espalha os online pela
// lista. Espelha o _last_session_terms do backend: mais recente primeiro é
// online, depois ended_at decrescente, e quem nunca teve sessão fica na ponta
// menos recente.
function lastSessionComparator<T>(
  isOnline: (row: T) => boolean,
  getEndedAt: (row: T) => string | null | undefined
): (a: T, b: T, order: 'asc' | 'desc') => number {
  return (a, b, order) => {
    // No cabeçalho, decrescente é "mais recente primeiro" — o inverso do que o
    // mesmo campo significa na API, onde é o ascendente que traz os online.
    const direction = order === 'desc' ? 1 : -1;

    if (isOnline(a) !== isOnline(b)) {
      return (isOnline(a) ? -1 : 1) * direction;
    }

    const endedA = getEndedAt(a);
    const endedB = getEndedAt(b);

    if (!endedA && !endedB) return 0;
    if (!endedA) return direction;
    if (!endedB) return -direction;

    return endedB.localeCompare(endedA) * direction;
  };
}

export {
  describeBootstrapEvent,
  describeHomeEvent,
  describeRouteGroup,
  describeSessionEvent,
} from './sessionEvents';
export type { Filter, Item };
export {
  delegatedAccessLabels,
  expiredDetail,
  formatDate,
  getErrorTypeTitle,
  getTerminatedByLabel,
  isDeletedUser,
  lastSessionComparator,
  normalizeStr,
  endReasonLabel,
  buildParams,
};
